/**
 * Rubric Edit Form Component
 * Reference implementation following UI Standards
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, Checkbox, FormControlLabel, Accordion,
  AccordionSummary, AccordionDetails, IconButton, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestoreIcon from '@mui/icons-material/Restore';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { RubricUpdate, ParsingConfig } from '../../types/rubric';
import ParsingConfigEditor from '../rubric/ParsingConfigEditor';
import { DEFAULT_PREAMBLE, DEFAULT_POSTAMBLE } from '../../constants/defaultPrompts';

const RubricEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<RubricUpdate>({
    name: '',
    description: '',
    criteria: {},
    is_fundamental: false,
    marking_preamble: '',
    marking_postamble: '',
    is_active: true,
    parsing_config: {
      enabled: false,
      question_delimiter: 'QUESTION',
      answer_delimiter: undefined,
      delimiter_type: 'prefix',
      number_pattern: '\\d+',
      case_sensitive: false,
      include_delimiter_in_content: false
    },
  });

  const [criteriaJson, setCriteriaJson] = useState('{}');
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchRubric = async () => {
      if (!id) {
        setError('No rubric ID provided');
        setFetchingData(false);
        return;
      }

      try {
        const response = await axios.get(`/api/rubrics/${id}`);
        const rubric = response.data;

        setFormData({
          name: rubric.name || '',
          description: rubric.description || '',
          criteria: rubric.criteria || {},
          is_fundamental: rubric.is_fundamental ?? false,
          marking_preamble: rubric.marking_preamble || '',
          marking_postamble: rubric.marking_postamble || '',
          is_active: rubric.is_active ?? true,
          parsing_config: rubric.parsing_config || {
            enabled: false,
            question_delimiter: 'QUESTION',
            answer_delimiter: undefined,
            delimiter_type: 'prefix',
            number_pattern: '\\d+',
            case_sensitive: false,
            include_delimiter_in_content: false
          },
        });

        setCriteriaJson(JSON.stringify(rubric.criteria || {}, null, 2));
      } catch (err: any) {
        console.error('Error fetching rubric:', err);
        if (err.response?.status === 404) {
          setError('Rubric not found');
        } else {
          setError('Failed to load rubric data. Please try again.');
        }
      } finally {
        setFetchingData(false);
      }
    };

    fetchRubric();
  }, [id]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};

    try {
      JSON.parse(criteriaJson);
    } catch (e) {
      errors.criteria = 'Criteria must be valid JSON';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Reset preamble to default
  const handleResetPreamble = () => {
    setFormData(prev => ({ ...prev, marking_preamble: DEFAULT_PREAMBLE }));
  };

  // Reset postamble to default
  const handleResetPostamble = () => {
    setFormData(prev => ({ ...prev, marking_postamble: DEFAULT_POSTAMBLE }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? null : Number(value)) : value)
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCriteriaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCriteriaJson(e.target.value);
    if (validationErrors.criteria) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.criteria;
        return newErrors;
      });
    }
  };

  const handleParsingConfigChange = (config: ParsingConfig) => {
    setFormData(prev => ({
      ...prev,
      parsing_config: config
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const payload: RubricUpdate = {};

      if (formData.name && formData.name.trim() !== '') payload.name = formData.name;
      if (formData.description && formData.description.trim() !== '') payload.description = formData.description;
      payload.criteria = JSON.parse(criteriaJson);
      if (formData.is_fundamental !== undefined) payload.is_fundamental = formData.is_fundamental;
      if (formData.marking_preamble && formData.marking_preamble.trim() !== '') payload.marking_preamble = formData.marking_preamble;
      if (formData.marking_postamble && formData.marking_postamble.trim() !== '') payload.marking_postamble = formData.marking_postamble;
      if (formData.is_active !== undefined) payload.is_active = formData.is_active;
      if (formData.parsing_config !== undefined) payload.parsing_config = formData.parsing_config;

      await axios.patch(`/api/rubrics/${id}`, payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/rubrics');
      }, 1500);
    } catch (err: any) {
      console.error('Error updating rubric:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to update rubric. Please check your input.');
        }
      } else {
        setError('Failed to update rubric. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !formData.name) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/rubrics')} sx={{ mt: 2 }}>
          Back to Rubrics
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/rubrics')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Rubrics
        </MuiLink>
        <Typography color="text.primary">Edit Rubric</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Edit Rubric</Typography>
        <Typography variant="body2" color="textSecondary">Update the rubric details</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Rubric updated successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth label="Rubric Name" name="name"
              value={formData.name || ''} onChange={handleChange}
              error={!!validationErrors.name} helperText={validationErrors.name}
              disabled={loading}
            />

            <TextField
              fullWidth label="Description" name="description"
              value={formData.description || ''} onChange={handleChange}
              disabled={loading} multiline rows={2}
            />

            <TextField
              fullWidth label="Criteria (JSON)" name="criteria"
              value={criteriaJson} onChange={handleCriteriaChange}
              error={!!validationErrors.criteria} helperText={validationErrors.criteria || 'Enter rubric criteria as JSON'}
              disabled={loading} multiline rows={4}
            />

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              AI Marking Instructions
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              These instructions control how the AI marks student submissions. Customize them for subject-specific instructions or different feedback styles. Use the restore button to reset to system defaults.
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle2">
                  Prompt Preamble
                </Typography>
                <Tooltip title="Reset to system default">
                  <IconButton size="small" onClick={handleResetPreamble} disabled={loading}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                name="marking_preamble"
                value={formData.marking_preamble || ''}
                onChange={handleChange}
                disabled={loading}
                multiline
                rows={6}
                helperText="Sets the AI's role and task. Tells the AI what it is (marking assistant) and what constraints apply (JSON output, use rubric only)."
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle2">
                  Prompt Postamble
                </Typography>
                <Tooltip title="Reset to system default">
                  <IconButton size="small" onClick={handleResetPostamble} disabled={loading}>
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                name="marking_postamble"
                value={formData.marking_postamble || ''}
                onChange={handleChange}
                disabled={loading}
                multiline
                rows={12}
                helperText="Provides detailed output formatting rules. Specifies the exact structure for feedback (summary + detailed breakdown) and includes an example for consistency."
              />
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControlLabel
                control={<Checkbox checked={formData.is_fundamental ?? false} onChange={handleChange} name="is_fundamental" disabled={loading} />}
                label="Fundamental Rubric"
              />
              <FormControlLabel
                control={<Checkbox checked={formData.is_active ?? true} onChange={handleChange} name="is_active" disabled={loading} />}
                label="Active"
              />
            </Stack>

            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Submission Parsing Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Configure how student submissions are parsed to extract individual questions and answers.
                  This helps with structured marking and question-by-question feedback.
                </Typography>
                {formData.parsing_config && (
                  <ParsingConfigEditor
                    value={formData.parsing_config}
                    onChange={handleParsingConfigChange}
                  />
                )}
              </AccordionDetails>
            </Accordion>

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/rubrics')} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Updating...' : 'Update Rubric'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default RubricEditForm;
